import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getOppMarketScreener,
  getOppOrderbookExchanges,
  type OppMarketScreenerItem,
  type OppMarketScreenerResponse,
  type OppMarketScreenerSortBy,
} from '@/services/oppDetectorApi';
import { hasCategory, renderCategoryLabel, categoryBadgeClass } from '@/lib/instrumentCategory';
import '../OppTop/OppTop.css';

const DEFAULT_LIMIT = 100;
const POLL_MS = 60000;
const DEFAULT_WINDOW_SECS = 300;

const WINDOW_OPTIONS = [
  { value: 300, label: '5 分钟' },
  { value: 600, label: '10 分钟' },
  { value: 1800, label: '30 分钟' },
  { value: 3600, label: '1 小时' },
] as const;

const SORT_OPTIONS: Array<{ value: OppMarketScreenerSortBy; label: string }> = [
  { value: 'price_change_pct', label: '价格涨跌幅' },
  { value: 'price_range_pct', label: '价格振幅' },
  { value: 'price_volatility_std', label: '价格波动 std' },
  { value: 'price_volatility_mad', label: '价格波动 MAD' },
  { value: 'volume_usdt', label: '成交额 (USDT)' },
  { value: 'volume_ratio', label: '相对放量' },
];

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(4)}%`;
}

function formatPrice(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 6 });
  if (a >= 1e-4) return v.toLocaleString('en-US', { maximumFractionDigits: 8 });
  return v.toExponential(4);
}

function formatVolumeUsdt(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(0);
}

function formatRatio(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '—';
  return `${v.toFixed(2)}x`;
}

function formatScore(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1) return v.toFixed(4);
  return v.toExponential(3);
}

function formatTsMs(tsMs: number): string {
  if (!Number.isFinite(tsMs) || tsMs <= 0) return '—';
  return new Date(tsMs).toLocaleString('zh-CN', { hour12: false });
}

function emptyPage(exchange: string): OppMarketScreenerResponse {
  return {
    exchange,
    window_secs: DEFAULT_WINDOW_SECS,
    sort_by: 'price_range_pct',
    total: 0,
    offset: 0,
    limit: DEFAULT_LIMIT,
    items: [],
  };
}

export function KlineScreener() {
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [exchange, setExchange] = useState('');
  const [windowSecs, setWindowSecs] = useState(DEFAULT_WINDOW_SECS);
  const [sortBy, setSortBy] = useState<OppMarketScreenerSortBy>('price_range_pct');
  const [minVolumeUsdt, setMinVolumeUsdt] = useState('');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<OppMarketScreenerResponse>(() => emptyPage(''));
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void getOppOrderbookExchanges()
      .then((list) => {
        if (!mounted.current) return;
        setExchanges(list);
        if (list.length > 0) {
          setExchange((prev) => prev || list[0]);
        }
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const parsedMinVolume = useMemo(() => {
    const trimmed = minVolumeUsdt.trim();
    if (!trimmed) return undefined;
    const next = Number(trimmed);
    return Number.isFinite(next) && next > 0 ? next : undefined;
  }, [minVolumeUsdt]);

  const load = useCallback(async () => {
    if (!exchange) {
      setPage(emptyPage(''));
      setLoading(false);
      return;
    }
    try {
      const data = await getOppMarketScreener({
        exchange,
        window_secs: windowSecs,
        sort_by: sortBy,
        min_volume_usdt: parsedMinVolume,
        limit,
        offset,
      });
      if (!mounted.current) return;
      setPage(data);
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [exchange, limit, offset, parsedMinVolume, sortBy, windowSecs]);

  useEffect(() => {
    mounted.current = true;
    void load();
    const t = autoRefresh ? window.setInterval(() => void load(), POLL_MS) : null;
    return () => {
      mounted.current = false;
      if (t != null) window.clearInterval(t);
    };
  }, [autoRefresh, load]);

  const resetOffset = useCallback(() => setOffset(0), []);

  const rows = page.items;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(page.total / Math.max(page.limit, 1))),
    [page.limit, page.total],
  );
  const currentPage = useMemo(
    () => Math.floor(page.offset / Math.max(page.limit, 1)) + 1,
    [page.limit, page.offset],
  );

  const copyInstId = useCallback((item: OppMarketScreenerItem) => {
    void navigator.clipboard.writeText(item.inst_id);
  }, []);

  return (
    <div className="opp-top-page">
      <header className="opp-top-header">
        <h2>K线筛选</h2>
        <p className="opp-top-desc">
          基于 opp-detector L1 mid 价格 K 线与 USDT 成交额，扫描单所合约的价格/成交量异动。
          数据来自 <code>/api/opp-detector/market/screener</code>，
          {autoRefresh ? `每 ${POLL_MS / 1000}s 自动刷新` : '自动刷新已停止'}。
        </p>
      </header>

      <div className="opp-top-toolbar">
        <button type="button" disabled={loading || !exchange} onClick={() => void load()}>
          立即刷新
        </button>
        <button type="button" onClick={() => setAutoRefresh((v) => !v)}>
          {autoRefresh ? '停止自动刷新' : '启动自动刷新'}
        </button>
        <label className="opp-top-filter-field">
          <span>交易所</span>
          <select
            value={exchange}
            onChange={(e) => {
              setExchange(e.target.value);
              resetOffset();
            }}
          >
            <option value="">请选择</option>
            {exchanges.map((ex) => (
              <option key={ex} value={ex}>
                {ex}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>统计窗口</span>
          <select
            value={windowSecs}
            onChange={(e) => {
              setWindowSecs(Number(e.target.value) || DEFAULT_WINDOW_SECS);
              resetOffset();
            }}
          >
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>排序指标</span>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as OppMarketScreenerSortBy);
              resetOffset();
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>最小成交额 USDT</span>
          <input
            value={minVolumeUsdt}
            onChange={(e) => {
              setMinVolumeUsdt(e.target.value);
              resetOffset();
            }}
            placeholder="可选"
          />
        </label>
        <label className="opp-top-filter-field">
          <span>每页条数</span>
          <input
            type="number"
            min={1}
            step={1}
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value);
              setLimit(Number.isFinite(next) && next > 0 ? Math.floor(next) : DEFAULT_LIMIT);
              resetOffset();
            }}
          />
        </label>
        <button type="button" disabled={loading || offset <= 0} onClick={() => setOffset((prev) => Math.max(0, prev - limit))}>
          上一页
        </button>
        <button
          type="button"
          disabled={loading || offset + limit >= page.total}
          onClick={() => setOffset((prev) => prev + limit)}
        >
          下一页
        </button>
        <p className="opp-top-meta">
          第 {currentPage} / {totalPages} 页，共 {page.total} 条
        </p>
        {lastOkAt != null && (
          <p className="opp-top-meta">上次成功：{new Date(lastOkAt).toLocaleString('zh-CN', { hour12: false })}</p>
        )}
      </div>

      {!exchange && <div className="opp-top-error">请选择一个交易所后再查询。</div>}
      {error && <div className="opp-top-error">{error}</div>}

      <div className="opp-top-table-wrap">
        {rows.length === 0 && !loading && !error && exchange ? (
          <div className="opp-top-empty">暂无数据（服务未启动或窗口内尚无 L1/成交样本）</div>
        ) : (
          <table className="opp-top-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Symbol</th>
                <th>inst_id</th>
                <th className="num">Close</th>
                <th className="num">涨跌幅</th>
                <th className="num">振幅</th>
                <th className="num">成交额</th>
                <th className="num">相对放量</th>
                <th className="num">排序分</th>
                <th>更新时间</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.symbol}-${r.inst_id}-${i}`}
                  className="opp-top-row-clickable"
                  onClick={() => copyInstId(r)}
                  title="点击复制 inst_id"
                >
                  <td>{page.offset + i + 1}</td>
                  <td>
                    <span className="opp-top-symbol-cell">
                      <span>{r.symbol}</span>
                      {hasCategory(r.category) && (
                        <span className={categoryBadgeClass(r.category)}>{renderCategoryLabel(r.category)}</span>
                      )}
                    </span>
                  </td>
                  <td>
                    <span title={r.inst_id}>{r.inst_id}</span>
                  </td>
                  <td className="num">{formatPrice(r.close)}</td>
                  <td className="num">{formatPct(r.price_change_pct)}</td>
                  <td className="num">{formatPct(r.price_range_pct)}</td>
                  <td className="num">{formatVolumeUsdt(r.volume_usdt)}</td>
                  <td className="num">{formatRatio(r.volume_ratio)}</td>
                  <td className="num">{formatScore(r.sort_score)}</td>
                  <td>{formatTsMs(r.ts_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
